import { defineStore } from 'pinia';
import { apiClient } from '@/lib/api/client';
import { OdooAccounts, OdooFindAccountsResponse } from '@/lib/api/odoo.api';

function getCachedAccounts(): OdooAccounts[] {
  try {
    const raw = localStorage.getItem('accounts-data');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const useAccountStore = defineStore('account', {
  state: () => ({
    loading: false,
    page: 0,
    pageSize: 50,
    response: {
      accounts: getCachedAccounts(),
      refresh: parseInt(localStorage.getItem('accounts-refresh-time') ?? '0', 10) || 0,
      now: new Date().getTime(),
    } as OdooFindAccountsResponse,
    searchQuery: '',
  }),

  getters: {
    data(): OdooAccounts[] {
      return this.listAccounts.slice(this.startIndex, this.endIndex);
    },

    listAccounts(state): OdooAccounts[] {
      const accounts = state.response?.accounts ?? [];
      const query = String(state.searchQuery ?? '');
      return accounts.filter((account) => {
        if (!account) return false;
        const ref = account.ref != null ? String(account.ref) : '';
        return ref.includes(query);
      });
    },

    refreshTime(state): number {
      return state.response.refresh || 0;
    },

    endIndex(state): number {
      return this.startIndex + state.pageSize;
    },

    startIndex(state): number {
      return state.page * state.pageSize;
    },

    totalPages(state): number {
      return Math.ceil(this.listAccounts.length / state.pageSize);
    },
  },

  actions: {
    async fetchAccounts(): Promise<void> {
      try {
        this.loading = true;

        const { data, response } = await apiClient.GET('/odoo/accounts');
        if (!response.ok) {
          throw new Error('Failed to fetch accounts');
        }

        this.response = data;
        localStorage.setItem('accounts-data', JSON.stringify(this.response.accounts));
        localStorage.setItem('accounts-refresh-time', this.response.refresh.toString());
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
});
